/**
 * Kept simulator runs: every Ask AI the backend followed to its end, listed across the
 * account and read one at a time, so a run can be reopened as it ended.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { SimulationProfileExtras } from '@/modules/simulator/sidebar/simulation-profile.store';
import type { RunMode } from '@/types/websocket.types';
import type { DescribedScenarioType, ScenarioRunHistoryItem, SimulatorRunOrigin } from './ai-function.service';

/** One kept simulator run, whole: the thread it sent, what came back, and the sidebar it ran with. */
export interface SimulatorRun extends ScenarioRunHistoryItem {
	sopId: string | null;
	origin: SimulatorRunOrigin;
	/** The sidebar's world as it stood for the run. */
	simulationProfile: Partial<SimulationProfileExtras> | null;
	/** The type a described run carried inline; null when it ran a stored scenario or none. */
	scenarioType: DescribedScenarioType | null;
	/** The reply's attributes the Run details card reads, as Brahma sent them. */
	details: Record<string, unknown> | null;
}

export interface SimulatorRunFilter {
	userId?: string;
	mode?: RunMode;
}

interface SimulatorRunPage {
	runs: SimulatorRun[];
	total: number;
}

export const simulatorRunService = {
	/** The account's kept simulator runs, newest first. */
	async fetchRuns(filter: SimulatorRunFilter, offset: number, limit: number): Promise<SimulatorRunPage> {
		const response = await api.get<ApiResponse<SimulatorRunPage>>(API_ENDPOINTS.AI_FUNCTIONS.SIMULATOR_RUNS, {
			params: { ...filter, offset, limit }
		});
		return response.data.data;
	},

	/** One kept run, whole. */
	async fetchRun(requestId: string): Promise<SimulatorRun> {
		const response = await api.get<ApiResponse<SimulatorRun>>(API_ENDPOINTS.AI_FUNCTIONS.SIMULATOR_RUN(requestId));
		return response.data.data;
	}
};
